import React from 'react';
import { useParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import PreRegisterPage, { STRINGS, detectLang, readDraft } from './PreRegisterPage';
import './PreRegisterPage.css';

/**
 * Boundary propio del formulario público.
 *
 * El boundary global (src/index.js) enseña un texto plano solo en castellano,
 * sin forma de volver atrás: el socio se queda mirando una pantalla muerta con
 * todo lo que había escrito perdido. Aquí, en cambio, el mensaje va en su idioma
 * y "Reintentar" recarga la página — el borrador vive en sessionStorage, así que
 * los datos tecleados vuelven solos (las fotos no: hay que rehacerlas).
 *
 * Recibe `venueToken` para etiquetar el evento en Sentry: la página es anónima,
 * así que sin esto no hay forma de saber qué club sufrió el fallo.
 */
export default class PreRegisterErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    Sentry.captureException(error, {
      tags: { area: 'pre-register', venue_token: this.props.venueToken || 'unknown' },
      extra: { componentStack: info?.componentStack },
    });
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    const lang = readDraft(this.props.venueToken).lang || detectLang();
    const t = STRINGS[lang] || STRINGS.es;

    return (
      <div className="prf-state">
        <div className="prf-state-card" role="alert">
          <h1 className="prf-state-title">{t.crashTitle}</h1>
          <p className="prf-state-body">{t.crashDesc}</p>
          <button
            type="button"
            className="prf-btn prf-btn--primary"
            onClick={() => window.location.reload()}
          >
            {t.crashRetry}
          </button>
        </div>
      </div>
    );
  }
}

/**
 * La ruta pública, ya envuelta. El boundary necesita el token del club (para
 * etiquetar el error y leer el borrador), y solo se puede leer dentro de la ruta.
 */
export function PreRegisterRoute() {
  const { venueToken } = useParams();
  return (
    <PreRegisterErrorBoundary venueToken={venueToken}>
      <PreRegisterPage />
    </PreRegisterErrorBoundary>
  );
}
